import React, { useCallback, useMemo } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { BonusTitle } from '@atlas/components';
import { NotificationManager, useSettings } from '@atlas/core';
import { Icon, Label } from '@atlas/ui';

import ModalPortal from 'common/ModalPortal/ModalPortal';

import {
  buildFinishedBonusDetailsRows,
  resolveFinishedBonusTitle,
  resolveFinishedBonusTitleHighlighted,
} from './buildFinishedBonusDetailsRows';
import {
  FINISHED_BONUS_DETAILS_SOURCE,
  finishedBonusPeriodDaysLabelKey,
} from './finishedBonusDetailsShared';
import { resolveFinishedBonusPlaceholderSrc } from './resolveFinishedBonusPlaceholderSrc';

const hasRetrievableFinishedBonusRowValue = row => {
  if (row.valueLabelKey) {
    return true;
  }

  if (row.valueDays != null) {
    return true;
  }

  const text = row.valueText;

  if (text == null) {
    return false;
  }

  return typeof text !== 'string' || text.trim() !== '';
};

const renderRowValue = row => {
  if (row.valueLabelKey) {
    return <Label message={row.valueLabelKey} />;
  }

  if (row.valueDays != null) {
    return (
      <>
        <span>{row.valueDays}</span>{' '}
        <Label
          className="finished-bonus-details__value--days"
          message={finishedBonusPeriodDaysLabelKey(row.valueDays)}
        />
      </>
    );
  }

  return <span>{row.valueText}</span>;
};

const FinishedBonusDetailsContent = ({
  imageUrl,
  placeholderImageUrl,
  rows,
  title,
  titleHighlighted,
}) => {
  const availableFinishedBonusRows = rows.filter(hasRetrievableFinishedBonusRowValue);
  const resolvedImageUrl = imageUrl || placeholderImageUrl;
  return (
    <div className="finished-bonus-details">
      <div className="finished-bonus-details__image-wrap">
        {resolvedImageUrl ? (
          <img alt="" className="finished-bonus-details__image" src={resolvedImageUrl} />
        ) : (
          <div className="finished-bonus-details__image-placeholder">
            <svg
              fill="none"
              height="102"
              viewBox="0 0 102 102"
              width="102"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M89.002 36.2342C87.3313 31.9403 84.9725 27.9985 82.032 24.5534C75.9464 17.4185 67.4493 12.4341 57.7992 10.8125C55.5838 10.4393 53.3208 10.2002 50.9992 10.2002C48.6775 10.2002 46.4143 10.4393 44.1992 10.8125C34.549 12.4341 26.0544 17.4171 19.9662 24.5507C17.0283 27.9973 14.6695 31.9377 12.999 36.2314C11.2137 40.8163 10.1992 45.7846 10.1992 51.0002C10.1992 55.7934 11.0677 60.3728 12.587 64.6453C14.1329 68.9911 16.3668 73.0047 19.2011 76.531C25.3292 84.1544 34.1371 89.4973 44.1992 91.1879C46.4143 91.5611 48.6775 91.8002 50.9992 91.8002C53.3208 91.8002 55.5838 91.5611 57.7992 91.1879C67.861 89.4971 76.6692 84.1542 82.7972 76.532C85.6315 73.0061 87.8654 68.9925 89.4111 64.6467C90.9304 60.3742 91.7992 55.7934 91.7992 51.0002C91.7992 45.786 90.7844 40.8177 89.002 36.2342ZM84.2156 38.9955L77.0808 43.1138C75.729 38.6513 73.2611 34.675 70.0021 31.498L77.2029 27.3409C80.2444 30.7065 82.6404 34.6497 84.2156 38.9955ZM72.815 51.0002C72.815 63.0291 63.0292 72.816 50.9992 72.816C38.9689 72.816 29.1833 63.0291 29.1833 51.0002C29.1833 38.9716 38.9691 29.1844 50.9992 29.1844C63.0292 29.1844 72.815 38.9716 72.815 51.0002ZM50.9992 15.6402C53.3259 15.6402 55.5971 15.8807 57.7992 16.3109V24.6382C55.621 24.0751 53.3498 23.7444 50.9992 23.7444C48.6483 23.7444 46.3771 24.0752 44.1992 24.6382V16.3109C46.401 15.8807 48.6722 15.6402 50.9992 15.6402ZM24.7977 27.3397L31.9989 31.498C28.7399 34.6736 26.2696 38.65 24.9174 43.1124L17.7827 38.9941C19.3604 34.6484 21.7564 30.7051 24.7977 27.3397ZM17.3711 61.883L24.6119 57.7034C25.7647 62.2389 28.0465 66.3177 31.1568 69.6287L23.9849 73.7683C21.0659 70.3086 18.8001 66.2858 17.3711 61.883ZM50.9992 86.3602C48.6722 86.3602 46.401 86.1197 44.1992 85.6895V77.3622C46.3771 77.9255 48.6483 78.256 50.9992 78.256C53.3498 78.256 55.621 77.9255 57.7992 77.3622V85.6895C55.5971 86.1197 53.3259 86.3602 50.9992 86.3602ZM78.0104 73.7683L70.8413 69.6287C73.9516 66.3189 76.2335 62.2389 77.3862 57.7034L84.627 61.8843C83.198 66.2858 80.9323 70.3086 78.0104 73.7683Z"
                fill="currentColor"
              />
              <path
                clipRule="evenodd"
                d="M51.1434 63.3757C50.8848 63.3757 50.6262 63.2279 50.5154 63.0063L46.1933 55.655L38.8421 51.3329C38.6204 51.2221 38.4727 50.9635 38.4727 50.7049C38.4727 50.4463 38.6204 50.1878 38.8421 50.0769L46.1933 45.7548L50.5154 38.4036C50.6262 38.1819 50.8848 38.0342 51.1434 38.0342C51.402 38.0342 51.6606 38.1819 51.7714 38.4036L56.0935 45.7548L63.4448 50.0769C63.6664 50.1878 63.8142 50.4463 63.8142 50.7049C63.8142 50.9635 63.6664 51.2221 63.4448 51.3329L56.0935 55.655L51.7714 63.0063C51.6606 63.2279 51.402 63.3757 51.1434 63.3757Z"
                fill="currentColor"
                fillRule="evenodd"
              />
            </svg>
          </div>
        )}
      </div>
      {title || titleHighlighted ? (
        <div className="finished-bonus-details__title">
          <BonusTitle name={title} nameHighlighted={titleHighlighted} />
        </div>
      ) : null}
      <div className="finished-bonus-details__section">
        <Label
          className="finished-bonus-details-popup__table-title"
          message="finished_bonuses_details"
        />
        <div className="finished-bonus-details__table">
          {availableFinishedBonusRows.map((row, index) => (
            <div className="finished-bonus-details__row" key={`${row.labelKey}-${index}`}>
              <span className="finished-bonus-details__key">
                <Label message={row.labelKey} />
              </span>
              <span
                className={cx('finished-bonus-details__value', {
                  'finished-bonus-details__value--danger': row.valueTone === 'danger',
                  'finished-bonus-details__value--success': row.valueTone === 'success',
                  'finished-bonus-details__value--wager': row.valueName === 'wager',
                })}
              >
                {renderRowValue(row)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

FinishedBonusDetailsContent.propTypes = {
  imageUrl: PropTypes.string,
  placeholderImageUrl: PropTypes.string,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  title: PropTypes.string,
  titleHighlighted: PropTypes.string,
};

const FinishedBonusDetailsPopup = ({ data = {}, id }) => {
  const { lang } = useSettings();
  const onNavigateClose = data.onNavigateClose;

  const handleClose = useCallback(() => {
    onNavigateClose?.();
    NotificationManager.removeNotification(id);
  }, [id, onNavigateClose]);

  const rows = useMemo(
    () =>
      buildFinishedBonusDetailsRows({
        bonus: data.bonus,
        extra: data.extra || {},
        freeSpin: data.freeSpin,
        lang,
        sourceType: data.sourceType,
      }),
    [data.bonus, data.extra, data.freeSpin, data.sourceType, lang],
  );

  const title = useMemo(
    () =>
      resolveFinishedBonusTitle({
        bonus: data.bonus,
        freeSpin: data.freeSpin,
        lang,
        title: data.title,
      }),
    [data.bonus, data.freeSpin, data.title, lang],
  );

  const titleHighlighted = useMemo(
    () =>
      resolveFinishedBonusTitleHighlighted({
        bonus: data.bonus,
        freeSpin: data.freeSpin,
        lang,
        title: data.title,
      }),
    [data.bonus, data.freeSpin, data.title, lang],
  );

  const placeholderImageUrl = useMemo(
    () =>
      resolveFinishedBonusPlaceholderSrc({
        bonus: data.bonus,
        sourceType: data.sourceType,
      }),
    [data.bonus, data.sourceType],
  );

  return (
    <ModalPortal backdropOff className="finished-bonus-details-popup-z">
      <div className="finished-bonus-details-mobile">
        <button
          aria-label="close"
          className="finished-bonus-details-mobile__backdrop"
          onClick={handleClose}
          type="button"
        />
        <div className="finished-bonus-details-mobile__sheet">
          <button
            className="finished-bonus-details-mobile__close"
            onClick={handleClose}
            type="button"
          >
            <Icon height={16} name="close" width={16} />
          </button>
          <FinishedBonusDetailsContent
            imageUrl={data.imageUrl}
            placeholderImageUrl={placeholderImageUrl}
            rows={rows}
            title={title}
            titleHighlighted={titleHighlighted}
          />
        </div>
      </div>
    </ModalPortal>
  );
};

FinishedBonusDetailsPopup.propTypes = {
  data: PropTypes.shape({
    bonus: PropTypes.object,
    extra: PropTypes.object,
    freeSpin: PropTypes.object,
    imageUrl: PropTypes.string,
    onNavigateClose: PropTypes.func,
    sourceType: PropTypes.oneOf(FINISHED_BONUS_DETAILS_SOURCE).isRequired,
    title: PropTypes.string,
    wallet: PropTypes.object,
  }),
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default FinishedBonusDetailsPopup;
